import React from 'react';
import { useTranslation } from 'react-i18next';

import { config } from 'core/constants';
import {
  createDateTimeCustomFormatter,
  createDateTimeNativeFormatter,
  createNumberFormatter,
} from './localization.business';
import {
  customDateTimeMask,
  defaultDateFormatConfig,
  defaultDateTimeFormatConfig,
  defaultTimeFormatConfig,
} from './localization.constants';
import { localizationContext } from './localization.context';
import { LocalizationContext } from './localization.model';

export const LocalizationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { i18n } = useTranslation();

  // Computed values
  const language = i18n?.language;

  // Local memoized context value (local state)
  const contextValue = React.useMemo<LocalizationContext>(() => {
    // Date time
    const formatDate = createDateTimeNativeFormatter(language, defaultDateFormatConfig);
    const formatTime = createDateTimeNativeFormatter(language, defaultTimeFormatConfig);
    const formatDateTime = createDateTimeNativeFormatter(language, defaultDateTimeFormatConfig);
    const formatDateTimeCustom = createDateTimeCustomFormatter(language);

    // Number
    const formatNumber = createNumberFormatter(language);
    const formatNumberSimplePrecision = createNumberFormatter(language, {
      maximumFractionDigits: config.FORMAT_NUMBER_SIMPLE_FRACTION_DIGITS,
    });
    const formatNumberMaxPrecision = createNumberFormatter(language, {
      maximumFractionDigits: config.FORMAT_NUMBER_PRECISION_FRACTION_DIGITS,
    });

    return {
      language,
      formatDate,
      formatTime,
      formatDateTime,
      formatDateTimeCustom,
      customDateTimeMask,
      formatNumber,
      formatNumberSimplePrecision,
      formatNumberMaxPrecision,
    };
  }, [language]);

  return (
    <localizationContext.Provider value={contextValue}>{children}</localizationContext.Provider>
  );
};
